/**
 * useChatOrchestrator — Composes all chat hooks, effects, and handlers.
 *
 * Extracted from ChatContainer to keep the component under 200 lines.
 * Returns all state and callbacks needed for the render tree.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAssistant } from "./useAssistant";
import { useChat } from "./useChat";
import { useAttachments } from "./useAttachments";
import { useChatMetadata } from "./useChatMetadata";
import { useAudioTranscription } from "./useAudioTranscription";
import { useMcpOAuth } from "./useMcpOAuth";
import { setApiConfig } from "../utils/api";
import { getCurrentTenant } from "../utils/roles";
import { getUsedBlocks } from "../utils/messageUtils";
import {
  sendChatCreated,
  getAllowedOrigins,
  sendUnreadReplyStatus,
  sendRunningStatus,
} from "../utils/embed-auth-bridge";
import {
  showUnreadReplyBadge,
  clearUnreadReplyBadge,
  showRunningIndicator,
  clearRunningIndicator,
} from "../utils/unreadIndicator";

export const useChatOrchestrator = (props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const envRetrieval = Boolean(process.env.REACT_APP_RETRIEVAL);
  const realtimeEnabled = false;
  const isEmbedded = new URLSearchParams(window.location.search).get("embed") === "true";

  // ---- Status modal ----
  const [statusModal, setStatusModal] = useState(false);
  const [streamingError, setStreamingError] = useState(null);

  const showStatusModal = useCallback(() => {
    setStatusModal(true);
    setTimeout(() => setStatusModal(false), 3500);
  }, []);

  const resetFeedback = useCallback(() => {
    document.dispatchEvent(new Event("resetFeedbackState"));
  }, []);

  // ---- API initialization ----
  useEffect(() => {
    const init = async () => {
      try {
        const tenant = getCurrentTenant();
        await setApiConfig(tenant);
      } catch (error) {
        console.error("Error initializing relay server:", error);
      }
    };
    init();
  }, []);

  // ---- Hooks ----
  const assistant = useAssistant({
    retrievalId: props.retrievalId,
    getRetrieval: props.getRetrieval,
    onError: showStatusModal,
    onNavigate: (value) =>
      navigate(
        {
          pathname: `/projects/${value}/`,
          search: window.location.search,
        },
        { replace: true },
      ),
  });

  const activeRetrievalId = props.retrievalId || assistant.selectedRetrieval;
  // A retrievalId supplied via the route on the initial mount (e.g. a deep link to
  // /projects/:idRetrieval) locks the agent for this session — the selector must not
  // be shown to switch away from it. Captured once: after picking an agent,
  // onNavigate below rewrites the URL to /projects/:id too, which would otherwise
  // make props.retrievalId look "fixed" on the next render even though it was a
  // free user choice, not a deep link.
  const initialRetrievalIdRef = useRef(props.retrievalId);
  const hasFixedRetrieval = Boolean(initialRetrievalIdRef.current);

  const chat = useChat({
    retrievalId: activeRetrievalId,
    streamingAvailable: assistant.streamingAvailable,
    asyncAvailable: assistant.asyncAvailable,
    initialChat: props.chat || null,
    iniMessage: assistant.iniMessage,
    addQuestionRetData: props.addQuestionRetData,
    addMessage: props.addMessage,
    addQuestionAsync: props.addQuestionAsync,
    addMessageAsync: props.addMessageAsync,
    getChat: props.getChat,
    getRetrieval: props.getRetrieval,
    resetAssistantStates: assistant.resetAssistantVisuals,
    setLoading: assistant.setLoading,
    setAllowAsk: assistant.setAllowAsk,
    getCachedAssistant: assistant.getCachedAssistant,
    applyAssistantData: assistant.applyAssistantData,
    onResetFeedback: resetFeedback,
    showStatusModal,
    setStreamingError,
    setStatusModal,
  });

  const attachmentsHook = useAttachments();
  const metadataHook = useChatMetadata();

  // ---- Language tracking ----
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  useEffect(() => {
    const handleLanguageChange = (lng) => setCurrentLanguage(lng);
    setCurrentLanguage(i18n.language);
    i18n.on("languageChanged", handleLanguageChange);
    return () => i18n.off("languageChanged", handleLanguageChange);
  }, [i18n]);

  const audio = useAudioTranscription({
    currentLanguage,
    realtimeEnabled,
    setInputValue: chat.setInputValue,
  });

  const { realtimeConfig } = audio.audioRecorder;
  const {
    items,
    isConnected,
    disconnectConversation,
    realtimeError,
    realtimeChatId,
    setRetrievalId,
    clearItems,
  } = realtimeConfig;

  const { openWaitingForAuthPopup } = useMcpOAuth({
    streamingAvailable: assistant.streamingAvailable,
    iniMessage: assistant.iniMessage,
    t,
    setMessages: chat.setMessages,
    setIsLoading: chat.setIsLoading,
    setQuestion: chat.setQuestion,
    setChatId: chat.setChatId,
    handleExecutionStatus: chat.handleExecutionStatus,
    setStreamingError,
    setStatusModal,
    showStatusModal,
  });

  // ---- Matrix mode ----
  const [activeMatrix, setActiveMatrix] = useState(false);
  const [activatingMatrix, setActivatingMatrix] = useState(false);
  const [matrixModal, setMatrixModal] = useState(false);

  // Wrap startNewChat to also reset attachments and metadata
  const handleStartNewChat = useCallback(async () => {
    attachmentsHook.resetAttachments();
    metadataHook.resetMetadata();
    await chat.startNewChat();
  }, [chat, attachmentsHook, metadataHook]);

  // Wrap retrieval change to also reset chat, attachments, metadata, audio
  const handleRetrievalChange = useCallback((value) => {
    chat.resetChatStates();
    attachmentsHook.resetAttachments();
    metadataHook.resetMetadata();
    audio.resetAudio();
    setActiveMatrix(false);
    setActivatingMatrix(false);
    assistant.handleRetrievalChange(value);
  }, [chat, attachmentsHook, metadataHook, audio, assistant]);

  const executeMatrixMode = async () => {
    setMatrixModal(false);
    setActivatingMatrix(true);
    try {
      const response = await props.addMatrixMode(
        chat.question.id,
        props.matrix_mode,
      );
      setActiveMatrix(true);
      chat.setMessages(response.data.messages);
      chat.setQuestion(response.data);
      assistant.setMaxMsgLength(response.data.max_msg_length);
    } catch (error) {
      showStatusModal(null, error?.customMessage);
      console.error("Error:", error);
    } finally {
      setActivatingMatrix(false);
    }
  };

  // ---- UI misc ----
  const [expandedReferences, setExpandedReferences] = useState({});

  // Update retrievalId in realtime connection
  useEffect(() => {
    if (activeRetrievalId) setRetrievalId(activeRetrievalId);
  }, [activeRetrievalId, setRetrievalId]);

  // Update chatId from realtime
  useEffect(() => {
    if (realtimeEnabled && realtimeChatId) chat.setChatId(realtimeChatId);
  }, [realtimeChatId, realtimeEnabled, chat]);

  // Embed mode: notify parent when a chat is first created (or restarted after startNewChat)
  useEffect(() => {
    if (!isEmbedded || !chat.chatId) return;

    const lastAssistantMsg = [...chat.messages]
      .reverse()
      .find((m) => m.role === "assistant" && !m.loading);

    sendChatCreated(chat.chatId, lastAssistantMsg?.content ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.chatId, isEmbedded]); // chat.messages intentionally excluded — fires on chatId transition only

  // Embed mode: parent reports are terminal for the current chat.
  useEffect(() => {
    if (!isEmbedded) return undefined;

    const handleMessage = (event) => {
      if (!getAllowedOrigins().includes(event.origin)) return;
      if (event.data?.type !== "REPORT_SUBMITTED") return;
      chat.handleExecutionStatus("ENDED");
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [chat, isEmbedded]);

  // Audio errors
  useEffect(() => {
    const handleAudioErrors = async () => {
      if (realtimeEnabled && realtimeError) {
        setStatusModal(true);
        await disconnectConversation();
        chat.setEnded(true);
      } else if (audio.audioRecorder.errorCode) {
        setStatusModal(true);
        setTimeout(() => {
          setStatusModal(false);
          audio.audioRecorder.resetError();
        }, 3500);
      }
    };
    handleAudioErrors();
  }, [realtimeError, audio.audioRecorder, disconnectConversation, realtimeEnabled, chat]);

  // Badge the browser tab while an async request is running (amber, always)
  const wasLoadingRef = useRef(chat.isLoading);
  // Read by the visibilitychange handler below, which fires on its own and
  // would otherwise use a stale closure over chat.isLoading/asyncAvailable —
  // a ref always reflects the latest value without re-subscribing the listener.
  const isRunningRef = useRef(false);
  useEffect(() => {
    isRunningRef.current = assistant.asyncAvailable && chat.isLoading;
  }, [assistant.asyncAvailable, chat.isLoading]);

  useEffect(() => {
    const wasLoading = wasLoadingRef.current;
    wasLoadingRef.current = chat.isLoading;

    if (assistant.asyncAvailable) {
      if (chat.isLoading) {
        if (isEmbedded) {
          sendRunningStatus(true);
        } else {
          showRunningIndicator();
        }
      } else if (wasLoading) {
        if (isEmbedded) {
          sendRunningStatus(false);
        } else {
          clearRunningIndicator();
        }
      }
    }

    if (wasLoading && !chat.isLoading && document.hidden) {
      if (isEmbedded) {
        sendUnreadReplyStatus(true);
      } else {
        showUnreadReplyBadge();
      }
    }
  }, [chat.isLoading, assistant.asyncAvailable, isEmbedded]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      // While still running, the amber dot must stay no matter how many
      // times the tab is left and revisited — only clear the green
      // "unread reply" dot here, and only once the request has finished.
      if (document.visibilityState === "visible" && !isRunningRef.current) {
        if (isEmbedded) {
          sendUnreadReplyStatus(false);
        } else {
          clearUnreadReplyBadge();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isEmbedded]);

  // Full reset handler — exposed for the component to wire to DOM events
  const handleFullReset = useCallback(() => {
    assistant.resetAssistantVisuals();
    chat.resetChatStates();
    setActiveMatrix(false);
    setActivatingMatrix(false);
    setExpandedReferences({});
    attachmentsHook.resetAttachments();
    metadataHook.resetMetadata();
    audio.resetAudio();
    clearItems();
    disconnectConversation();
  }, [assistant, chat, clearItems, disconnectConversation, attachmentsHook, metadataHook, audio]);

  // Expand all references if extendedReferences prop
  useEffect(() => {
    if (props.extendedReferences && chat.messages.length > 0) {
      const initialExpanded = {};
      chat.messages.forEach((msg, index) => {
        if (getUsedBlocks(msg).length > 0) {
          initialExpanded[index] = true;
        }
      });
      setExpandedReferences(initialExpanded);
    }
  }, [chat.messages, props.extendedReferences]);

  // Track realtime transcription items
  useEffect(() => {
    audio.updateTranscriptionHistory(items);
  }, [items, audio]);

  // ---- Handlers ----

  const toggleReferences = (messageIndex) => {
    setExpandedReferences((prev) => ({
      ...prev,
      [messageIndex]: !prev[messageIndex],
    }));
  };

  const handleSendMessage = () => {
    chat.sendMessage({
      messageFiles: attachmentsHook.messageFiles,
      filesMetadata: attachmentsHook.filesMetadata,
      fileSpecificMetadata: attachmentsHook.fileSpecificMetadata,
      metadata: metadataHook.metadata,
      onMessageSent: () => {
        attachmentsHook.recordSentAttachments(chat.messages.length);
        metadataHook.recordSentMetadata(chat.messages.length);
        attachmentsHook.clearAfterSend();
        // Reset file input DOM element (component-layer concern)
        const fileInput = document.getElementById("file-input");
        if (fileInput) fileInput.value = "";
      },
    });
  };

  // ---- Error text ----
  const errorText = streamingError
    ? streamingError.type === "chatRunning"
      ? "chatRunning"
      : streamingError.message || "systemError"
    : realtimeError
      ? realtimeError.message || String(realtimeError)
      : audio.audioRecorder.errorCode
        ? audio.audioRecorder.errorCode
        : "systemError";
  const translated = t(errorText);
  const finalErrorText = translated !== errorText ? translated : errorText;

  const getErrorMessage = () => {
    switch (chat.executionStatus) {
      case "ERROR": return t("errorTechnical");
      case "ENDED_MAX_INTERACTIONS": return t("errorMaxInteractions");
      case "FORCED_ENDED": return t("errorForcedEnd");
      case "ENDED": return t("endedConversation");
      default: return t("systemError");
    }
  };

  const getModalMessage = () => {
    if (chat.customErrorMessage) return chat.customErrorMessage;
    if (realtimeError || audio.audioRecorder.errorCode) return finalErrorText;
    return getErrorMessage();
  };

  return {
    // Config
    envRetrieval,
    realtimeEnabled,
    isEmbedded,
    activeRetrievalId,
    hasFixedRetrieval,
    // Hooks
    assistant,
    chat,
    attachmentsHook,
    metadataHook,
    audio,
    // Realtime
    items,
    isConnected,
    realtimeError,
    realtimeConfig,
    // Matrix
    activeMatrix,
    activatingMatrix,
    matrixModal,
    setMatrixModal,
    executeMatrixMode,
    // UI state
    expandedReferences,
    // Modals
    statusModal,
    streamingError,
    setStatusModal,
    setStreamingError,
    // Handlers
    handleStartNewChat,
    handleRetrievalChange,
    handleSendMessage,
    handleFullReset,
    toggleReferences,
    getErrorMessage,
    getModalMessage,
    openWaitingForAuthPopup,
    t,
  };
};
